import {
  createFileRoute,
  notFound,
  Outlet,
  useBlocker,
} from '@tanstack/react-router';
import {Theme} from '@chakra-ui/react';
import {z} from 'zod';
import {useRef} from 'react';
import 'uplot/dist/uPlot.min.css';
import {seo} from '../utils/seo';
import {
  BluetoothContext,
  NoiseBuffersContext,
  NoiseLiveContext,
} from '../components/noise/context';
import {useNoiseStream} from '../components/noise/useNoiseStream';
import {useBleDevice} from '../components/noise/useBleDevice';
import {createServerFn} from '@tanstack/react-start';
import {crewAuth} from '../server/crewAuth';
import {
  and,
  asc,
  count,
  desc,
  eq,
  gt,
  gte,
  inArray,
  isNull,
  lt,
  lte,
  notExists,
  sql,
  type SQL,
} from 'drizzle-orm';
import type {AnyPgColumn} from 'drizzle-orm/pg-core';
import {db, isUniqueViolation} from '../server/db.server';
import {
  area,
  bandPlaying,
  device,
  noiseLocation,
  noiseLocationAssignment,
  noiseLocationLimit,
  noiseProject,
  noiseTag,
} from '../server/db/schema';
import {noiseTagDedupeKey} from '../server/noiseTagKey.server';
import {deviceAssignments, projectLogs} from '../server/noiseHistory.server';
import {Toaster} from '../components/chakra-snippets/toaster';
import {END_BEFORE_START} from '../components/noise/timeframe';
import {
  isLimitSeries,
  limitLine,
  type LimitSeries,
} from '../components/noise/limitLines';

// The section's data layer lives in this layout file and is imported by the leaf
// routes, as in crew.produkte.tsx. Every DateTime crosses the wire as epoch ms
// (like deviceLastSeen and HistoryData) and comes back in as an ISO string (like
// the ?start/?end search params), so no Date ever has to survive serialization.

export const listNoiseProjects = createServerFn()
  .middleware([crewAuth])
  .handler(async () => {
    // Newest festival first — that's the one you're almost always after.
    const projects = await db
      .select({
        id: noiseProject.id,
        name: noiseProject.name,
        start: noiseProject.start,
        end: noiseProject.end,
        locationCount: count(noiseLocation.id),
      })
      .from(noiseProject)
      .leftJoin(noiseLocation, eq(noiseLocation.projectId, noiseProject.id))
      .groupBy(noiseProject.id)
      .orderBy(desc(noiseProject.start), asc(noiseProject.name));
    return projects.map((p) => ({
      ...p,
      start: p.start.getTime(),
      end: p.end.getTime(),
    }));
  });

const isoInstant = z
  .string()
  .refine((v) => !Number.isNaN(Date.parse(v)), 'Invalid date');

// Exported because the create dialogs validate the same field client-side; the
// length cap and the message must not be able to drift apart, or an over-long
// name becomes a server rejection with nothing to attach it to.
export const noiseName = z.string().trim().min(1, 'Name required').max(60);

export const createNoiseProjectInput = z
  .object({
    name: noiseName,
    start: isoInstant,
    end: isoInstant,
  })
  .refine((v) => Date.parse(v.end) > Date.parse(v.start), {
    message: END_BEFORE_START,
    path: ['end'],
  });

export const createNoiseProject = createServerFn()
  .middleware([crewAuth])
  .validator(createNoiseProjectInput)
  .handler(async ({data}) =>
    // Returns the id so the dialog can drop the user straight into the project
    // they just created (which is why they created it).
    db
      .insert(noiseProject)
      .values({
        name: data.name,
        start: new Date(data.start),
        end: new Date(data.end),
      })
      .returning({id: noiseProject.id})
      .then(([created]) => created),
  );

export const loadNoiseProject = createServerFn()
  .middleware([crewAuth])
  .validator(z.object({projectId: z.string().min(1)}))
  .handler(async ({data}) => {
    const project = await db.query.noiseProject.findFirst({
      where: eq(noiseProject.id, data.projectId),
      columns: {id: true, name: true, start: true, end: true},
      with: {
        // Unordered on purpose: what order locations are shown in is decided in one
        // place, and it isn't here (see compareLocations). Postgres would sort
        // `Bühne 10` before `Bühne 2`, and a second opinion in the query is a second
        // thing to keep in step with the first.
        noiseLocations: {
          columns: {
            id: true,
            locationName: true,
            latitude: true,
            longitude: true,
          },
          with: {
            // The whole history, not just the open rows: which monitor stood here is
            // a question the page asks about the instant it is showing, and it
            // resolves that client-side (see assignmentsAt). `end == null` still
            // means "standing here right now", which is what live mode reads.
            //
            // Deliberately not filtered to the project's window either. Out-of-window
            // rows never match an in-window instant, and one case needs them: a device
            // assigned after a finished project's end has an open assignment starting
            // past the window, and live mode must still show it standing there.
            noiseLocationAssignments: {
              orderBy: asc(noiseLocationAssignment.start),
              columns: {id: true, deviceId: true, start: true, end: true},
              with: {device: {columns: {lastSeen: true}}},
            },
            // What each place is permitted. They travel with the location because they
            // belong to it, so the card's chart draws them and its ⋮ edits them without
            // either fetching per card.
            noiseLocationLimits: {
              orderBy: asc(noiseLocationLimit.start),
              columns: {
                id: true,
                series: true,
                decibels: true,
                start: true,
                end: true,
              },
            },
          },
        },
        // Every tag in the project, whatever it applies to: which of them a location's
        // chart shades is decided client-side against the monitors it has had (see
        // locationTags), so one query serves every card.
        noiseTags: {
          orderBy: asc(noiseTag.start),
          columns: {
            id: true,
            type: true,
            text: true,
            locationId: true,
            deviceId: true,
            start: true,
            end: true,
          },
        },
      },
    });
    if (!project) throw notFound();
    return {
      id: project.id,
      name: project.name,
      start: project.start.getTime(),
      end: project.end.getTime(),
      // Browser-key for the Maps JS API, shipped to the client the same way the
      // booking detail route does it.
      apiKey: process.env.GOOGLE_MAPS_API_KEY ?? null,
      locations: project.noiseLocations.map((l) => ({
        id: l.id,
        locationName: l.locationName,
        latitude: l.latitude,
        longitude: l.longitude,
        assignments: l.noiseLocationAssignments.map((a) => ({
          id: a.id,
          deviceId: a.deviceId,
          // Resolved here and nowhere else: a null start means the event's own (see the
          // schema), and every reader of a placement's window wants an instant — the
          // charts' clipping, assignmentsAt, the overlap warnings. This is the one place
          // that has both the row and the project in hand, so it is the one place that
          // has to know.
          start: a.start?.getTime() ?? project.start.getTime(),
          // Whether that came from the row or from the event, which the resolved number
          // above cannot say. Only the assignments dialog asks: a blank field is what
          // "from the beginning, whenever that turns out to be" looks like, and writing
          // the resolved instant back would pin a placement that was meant to follow.
          startsWithProject: a.start == null,
          // Null means still standing there; every other field is epoch ms, as
          // everything that crosses this wire is.
          end: a.end?.getTime() ?? null,
          lastSeen: a.device.lastSeen?.getTime() ?? null,
        })),
        // Both bounds resolved and the series narrowed by the shared helper (see
        // limitLine), which the device page's loader calls too — so a limit reaches a
        // chart as the same shape from either end, and a blank bound means the edge of
        // the event in one place rather than in three.
        //
        // Unlike a placement, whose open end is genuinely unresolvable ("still standing"),
        // *both* of a limit's blanks are the event's own — so both are resolved, and the
        // two flags beside them are what the editor needs to leave a field blank when it
        // was blank. Writing a resolved instant back would pin a limit that was meant to
        // follow the event's dates.
        limits: l.noiseLocationLimits.flatMap((x) => {
          const line = limitLine(x, project);
          return line
            ? [
                {
                  id: x.id,
                  ...line,
                  startsWithProject: x.start == null,
                  endsWithProject: x.end == null,
                },
              ]
            : [];
        }),
      })),
      tags: project.noiseTags.map((t) => ({
        id: t.id,
        type: t.type,
        text: t.text,
        locationId: t.locationId,
        deviceId: t.deviceId,
        start: t.start.getTime(),
        // Null is a marker at `start`, not "until the end of the event".
        end: t.end?.getTime() ?? null,
      })),
    };
  });

// Coordinates are plain numbers, never z.coerce.number(): Number('') is 0, so a
// coercing schema would turn an empty field into a valid point off West Africa.
// The dialog validates its strings first and only then converts.
export const createNoiseLocationInput = z.object({
  projectId: z.string().min(1),
  locationName: noiseName,
  latitude: z.number().min(-90).max(90),
  longitude: z.number().min(-180).max(180),
});

export const createNoiseLocation = createServerFn()
  .middleware([crewAuth])
  .validator(createNoiseLocationInput)
  .handler(async ({data}) => {
    // The FK would reject an unknown project anyway, but a 404 is the honest
    // answer for a stale page.
    const project = await db.query.noiseProject.findFirst({
      where: eq(noiseProject.id, data.projectId),
      columns: {id: true},
    });
    if (!project) throw notFound();
    const [created] = await db
      .insert(noiseLocation)
      .values(data)
      .returning({id: noiseLocation.id});
    return created;
  });

// A location and everything that is only about it: the monitors placed there, its limits,
// and the tags crew pinned to it. One transaction, since the foreign keys have no cascade and
// a half-deleted place — limits with nothing to belong to, or a location that lost its
// placements but not itself — is worse than either outcome.
//
// The monitors themselves are untouched, and so is what they recorded: a placement is only
// the claim that a device stood here, and its logs belong to the device. deleteMany
// throughout, so a double press or a stale page deletes nothing twice.
export const deleteNoiseLocation = createServerFn()
  .middleware([crewAuth])
  .validator(z.object({locationId: z.string().min(1)}))
  .handler(async ({data: {locationId}}) => {
    await db.transaction(async (tx) => {
      await tx
        .delete(noiseLocationAssignment)
        .where(eq(noiseLocationAssignment.locationId, locationId));
      await tx
        .delete(noiseLocationLimit)
        .where(eq(noiseLocationLimit.locationId, locationId));
      await tx.delete(noiseTag).where(eq(noiseTag.locationId, locationId));
      await tx.delete(noiseLocation).where(eq(noiseLocation.id, locationId));
    });
  });

// Every stored level of one project, in one payload: the page then answers its own
// questions locally (see projectLogs.ts). No window and no weighting in the input —
// the whole event travels, so scrubbing, cropping and the header's pickers never
// come back here. Only sent when live mode is off.
export const noiseProjectLogs = createServerFn()
  .middleware([crewAuth])
  .validator(z.object({projectId: z.string().min(1)}))
  .handler(async ({data}) => projectLogs(data.projectId));

// The monitors free to place in one project: those with no placement overlapping its
// dates, in this project or any other. A monitor hangs in one place at a time, so a
// placement anywhere during the event takes it out of the running for the whole event.
//
// Both bounds of a placement resolved the way the schema means them — a blank start is its
// event's start and a blank end its event's end. Testing for an open row instead (`end` is
// null) read every monitor ever placed "for the whole time" as standing there forever, so
// one festival left the whole fleet unplaceable in every project after it.
export const assignableNoiseDevices = createServerFn()
  .middleware([crewAuth])
  .validator(z.object({projectId: z.string().min(1)}))
  .handler(async ({data: {projectId}}) => {
    const target = await db.query.noiseProject.findFirst({
      where: eq(noiseProject.id, projectId),
      columns: {start: true, end: true},
    });
    if (!target) throw notFound();
    const devices = await db
      .select({id: device.id, lastSeen: device.lastSeen})
      .from(device)
      .where(
        and(
          eq(device.type, 'NOISE_MONITOR'),
          notExists(
            db
              .select({one: sql`1`})
              .from(noiseLocationAssignment)
              .innerJoin(
                noiseLocation,
                eq(noiseLocation.id, noiseLocationAssignment.locationId),
              )
              .innerJoin(
                noiseProject,
                eq(noiseProject.id, noiseLocation.projectId),
              )
              .where(
                and(
                  eq(noiseLocationAssignment.deviceId, device.id),
                  lt(
                    sql`coalesce(${noiseLocationAssignment.start}, ${noiseProject.start})`,
                    target.end,
                  ),
                  gt(
                    sql`coalesce(${noiseLocationAssignment.end}, ${noiseProject.end})`,
                    target.start,
                  ),
                ),
              ),
          ),
        ),
      )
      .orderBy(asc(device.id));
    return devices.map((d) => ({
      id: d.id,
      lastSeen: d.lastSeen?.getTime() ?? null,
    }));
  });

// Every monitor there is, assigned or not — what the landing page lists and what the
// assignments dialog picks from. Deliberately not `assignableNoiseDevices`: recording a
// placement that has already ended is half of what that dialog is for, and the device
// standing somewhere today is usually the one whose past you are correcting.
//
// Where each one is standing rides along, because that is most of what a list of
// monitors is read for — "which of these is at the Hauptbühne" is the question, and a
// list that made you open every device page to answer it would not be worth having. One
// extra findMany for the whole set (see deviceAssignments), so the two pickers pay a
// small join they then ignore rather than this being split into two near-identical
// server functions that both have to be kept in step.
export const noiseMonitorDevices = createServerFn()
  .middleware([crewAuth])
  .handler(async () => {
    const [devices, assignments] = await Promise.all([
      db
        .select({id: device.id, lastSeen: device.lastSeen})
        .from(device)
        .where(eq(device.type, 'NOISE_MONITOR'))
        .orderBy(asc(device.id)),
      deviceAssignments(),
    ]);
    return devices.map((d) => ({
      id: d.id,
      lastSeen: d.lastSeen?.getTime() ?? null,
      // Null for a monitor in a cupboard, which is the ordinary state of one between
      // festivals.
      assignment: assignments.get(d.id) ?? null,
    }));
  });

// Named here rather than restated by every reader: the row shape is this function's to
// define, and a list, a menu and a dialog all render one.
export type NoiseMonitorDevice = Awaited<
  ReturnType<typeof noiseMonitorDevices>
>[number];

// A blank bound, stored blank. Both ends of a placement mean "the event's own" when they
// are null (see the schema), so neither writer resolves anything and neither needs to read
// the project to save a row — which is the point: a placement typed as "the whole time"
// keeps following the event's dates instead of being pinned to whatever they were on the
// day somebody filled the form in.
const instantOrNull = (ms: number | null | undefined): Date | null =>
  ms == null ? null : new Date(ms);

export const assignNoiseDevice = createServerFn()
  .middleware([crewAuth])
  .validator(
    z.object({
      locationId: z.string().min(1),
      deviceId: z.string().min(1),
      // Epoch ms, both optional: an omitted bound means the edge of the event, so
      // picking a device and nothing else records "stood here the whole time".
      start: z.number().int().nullish(),
      end: z.number().int().nullish(),
    }),
  )
  .handler(async ({data}) => {
    const [location, monitor] = await Promise.all([
      db.query.noiseLocation.findFirst({
        where: eq(noiseLocation.id, data.locationId),
        columns: {id: true},
      }),
      db.query.device.findFirst({
        where: eq(device.id, data.deviceId),
        columns: {id: true, type: true},
      }),
    ]);
    if (!location) throw notFound();
    if (!monitor || monitor.type !== 'NOISE_MONITOR') {
      throw new Error('Unknown noise monitor.');
    }

    // Nothing else is touched. This used to close the device's other open rows so
    // that `end == null` could mean "is here now" — one open row per device. With
    // the windows editable by hand that invariant is neither enforceable nor
    // wanted, so the dialog warns about the overlap instead of the server quietly
    // rewriting a row nobody opened (see overlappingAssignments in projectView.ts).
    await db.insert(noiseLocationAssignment).values({
      locationId: data.locationId,
      deviceId: data.deviceId,
      start: instantOrNull(data.start),
      end: instantOrNull(data.end),
    });
  });

// One row's window, as typed. Times are taken at face value — including ones in the
// future and ones that overlap another placement — because this is a record of where
// a monitor stood, and the person filling it in knows better than a clamp would.
export const updateNoiseAssignment = createServerFn()
  .middleware([crewAuth])
  .validator(
    z.object({
      assignmentId: z.string().min(1),
      start: z.number().int().nullable(),
      end: z.number().int().nullable(),
    }),
  )
  .handler(async ({data}) => {
    // One write, whatever was typed: a blank bound is a blank column now, so an edit that
    // clears the start no longer has to walk down to the event and back for a value to
    // put there.
    const updated = await db
      .update(noiseLocationAssignment)
      .set({start: instantOrNull(data.start), end: instantOrNull(data.end)})
      .where(eq(noiseLocationAssignment.id, data.assignmentId))
      .returning({id: noiseLocationAssignment.id});
    // Gone under a stale page: say so rather than report a save that didn't happen.
    if (updated.length === 0) throw notFound();
  });

// Removes the row rather than closing it: a placement that never happened should
// leave no window behind, since assignmentsAt and the log queries both read every
// row there is. Ending one is an edit of its `end`, which is the dialog's other job.
//
// deleteMany, so a double-tap or a stale list deletes nothing twice rather than
// throwing on a row that has already gone.
export const deleteNoiseAssignment = createServerFn()
  .middleware([crewAuth])
  .validator(z.object({assignmentId: z.string().min(1)}))
  .handler(async ({data}) => {
    await db
      .delete(noiseLocationAssignment)
      .where(eq(noiseLocationAssignment.id, data.assignmentId));
  });

// A dB figure, and never z.coerce.number() for the reason the coordinates aren't
// either (see createNoiseLocationInput): Number('') is 0, and a coercing schema would
// turn an emptied field into a limit of absolute silence — the one wrong value that
// looks like a real one. The dialog parses its string and only then sends a number.
//
// Bounded loosely rather than to what a permit plausibly says: the point of the range
// is to catch the finger that typed the year into the field, not to have an opinion
// about how loud an event is allowed to be.
//
// The pair is exported because the dialog's field checks it too — not to enforce it a
// second time, but so a typo leaves Save disabled rather than producing a rejection with
// nothing attached to it. Off the schema's own numbers, so widening the range here widens
// what the field will hold rather than leaving it quietly refusing values the server takes.
export const NOISE_LIMIT_DB = {min: 0, max: 200};

export const noiseLimitDecibels = z
  .number()
  .min(NOISE_LIMIT_DB.min)
  .max(NOISE_LIMIT_DB.max);

// Which series the figure is written against — one of the table's (see isSeriesKey) or a
// weighting's `Leq,T` (see LimitSeries). The column is text, so this is the only thing
// standing between a typo in a request and a row nothing can draw — and it is `z.custom`
// rather than a `z.enum` of the names so that the list stays derived from the table rather
// than spelled out a second time here.
const noiseLimitSeries = z.custom<LimitSeries>(
  (v) => typeof v === 'string' && isLimitSeries(v),
  'Unknown series',
);

export const createNoiseLimit = createServerFn()
  .middleware([crewAuth])
  .validator(
    z.object({
      locationId: z.string().min(1),
      series: noiseLimitSeries,
      decibels: noiseLimitDecibels,
      // Both optional, as on a placement: a number and nothing else records "this is
      // the limit for the whole event".
      start: z.number().int().nullish(),
      end: z.number().int().nullish(),
    }),
  )
  .handler(async ({data}) => {
    // The FK would reject an unknown location anyway; a 404 is the honest answer for
    // a page whose project has been deleted under it.
    const location = await db.query.noiseLocation.findFirst({
      where: eq(noiseLocation.id, data.locationId),
      columns: {id: true},
    });
    if (!location) throw notFound();
    await db.insert(noiseLocationLimit).values({
      locationId: data.locationId,
      series: data.series,
      decibels: data.decibels,
      start: instantOrNull(data.start),
      end: instantOrNull(data.end),
    });
  });

// Unlike a placement — where which device a saved row is about is not editable, since
// that is a different placement — everything about a limit is. The series is as much a
// correction as the number: a permit read as an LAeq that turns out to be written against
// LCpeak is the same row with the wrong quantity on it, not a different limit.
//
// Nullable and not nullish on the bounds: an update says what the row now is in full, so
// clearing one is a stated null rather than an omission.
export const updateNoiseLimit = createServerFn()
  .middleware([crewAuth])
  .validator(
    z.object({
      limitId: z.string().min(1),
      series: noiseLimitSeries,
      decibels: noiseLimitDecibels,
      start: z.number().int().nullable(),
      end: z.number().int().nullable(),
    }),
  )
  .handler(async ({data}) => {
    const updated = await db
      .update(noiseLocationLimit)
      .set({
        series: data.series,
        decibels: data.decibels,
        start: instantOrNull(data.start),
        end: instantOrNull(data.end),
      })
      .where(eq(noiseLocationLimit.id, data.limitId))
      .returning({id: noiseLocationLimit.id});
    // Gone under a stale page: say so rather than report a save that didn't happen.
    if (updated.length === 0) throw notFound();
  });

// deleteMany, so a double-tap or a stale list deletes nothing twice.
export const deleteNoiseLimit = createServerFn()
  .middleware([crewAuth])
  .validator(z.object({limitId: z.string().min(1)}))
  .handler(async ({data}) => {
    await db
      .delete(noiseLocationLimit)
      .where(eq(noiseLocationLimit.id, data.limitId));
  });

// What a tag applies to, as the one field that says so: a monitor, a place, or the whole
// event. The row stores it as whichever of its keys is set (see the schema); the wire
// states it outright so a request cannot name a device *and* a location and leave the
// reader to guess which was meant.
const noiseTagScope = z.discriminatedUnion('kind', [
  z.object({kind: z.literal('device'), deviceId: z.string().min(1)}),
  z.object({kind: z.literal('location'), locationId: z.string().min(1)}),
  z.object({kind: z.literal('event')}),
]);

export type NoiseTagScope = z.infer<typeof noiseTagScope>;

// `= value`, or `IS NULL` for a null one: a tag's unset scope key is a NULL, and
// `= NULL` matches nothing.
const eqOrNull = (column: AnyPgColumn, value: string | null): SQL =>
  value == null ? isNull(column) : eq(column, value);

export const createNoiseTag = createServerFn()
  .middleware([crewAuth])
  .validator(
    z
      .object({
        projectId: z.string().min(1),
        type: z.enum(['IGNORE', 'COMMENT']),
        scope: noiseTagScope,
        start: z.number().int(),
        // Null is a marker: an instant rather than a range.
        end: z.number().int().nullable(),
        text: z.string().trim().max(2000).nullish(),
      })
      .refine((v) => v.end == null || v.end > v.start, {
        message: END_BEFORE_START,
        path: ['end'],
      }),
  )
  .handler(async ({data, context}) => {
    const viewerId = context.viewer?.id;
    if (!viewerId) throw new Error('Unauthorized');
    const {scope} = data;
    // A tag on a place belongs to that place's project, so a location from another
    // project is as unknown here as one that doesn't exist.
    if (scope.kind === 'location') {
      const location = await db.query.noiseLocation.findFirst({
        where: and(
          eq(noiseLocation.id, scope.locationId),
          eq(noiseLocation.projectId, data.projectId),
        ),
        columns: {id: true},
      });
      if (!location) throw notFound();
    } else {
      const project = await db.query.noiseProject.findFirst({
        where: eq(noiseProject.id, data.projectId),
        columns: {id: true},
      });
      if (!project) throw notFound();
    }
    const locationId = scope.kind === 'location' ? scope.locationId : null;
    const deviceId = scope.kind === 'device' ? scope.deviceId : null;

    // An ignored range that overlaps or touches one already set aside for the same thing
    // is folded into it rather than stored beside it: two ranges over one stretch say no
    // more than one does, and each would be its own "Un-ignore" to find on the chart.
    // Only the same scope — a device's range inside the location's still has to outlive
    // the location's being taken back. Every overlapping one at once, so a range bridging
    // two leaves one, as a fresh row over all of them. In a transaction, so the widened
    // range and the deletes of the ones it swallowed land together or not at all.
    await db.transaction(async (tx) => {
      const overlapping =
        data.type === 'IGNORE' && data.end != null
          ? await tx
              .select({
                id: noiseTag.id,
                start: noiseTag.start,
                end: noiseTag.end,
              })
              .from(noiseTag)
              .where(
                and(
                  eq(noiseTag.projectId, data.projectId),
                  eq(noiseTag.type, 'IGNORE'),
                  eqOrNull(noiseTag.locationId, locationId),
                  eqOrNull(noiseTag.deviceId, deviceId),
                  lte(noiseTag.start, new Date(data.end)),
                  gte(noiseTag.end, new Date(data.start)),
                ),
              )
          : [];
      if (overlapping.length > 0) {
        await tx.delete(noiseTag).where(
          inArray(
            noiseTag.id,
            overlapping.map((t) => t.id),
          ),
        );
      }
      const tag = {
        projectId: data.projectId,
        locationId,
        deviceId,
        type: data.type,
        // Widened over whatever it swallowed; with nothing overlapping, its own.
        start: new Date(
          Math.min(data.start, ...overlapping.map((t) => t.start.getTime())),
        ),
        end: instantOrNull(
          data.end == null
            ? null
            : Math.max(data.end, ...overlapping.map((t) => t.end!.getTime())),
        ),
        // Only a comment says anything; an ignored range is its own explanation.
        text: data.type === 'COMMENT' ? data.text || null : null,
      };
      // A tag already there word for word is the one being asked for, so writing it again
      // is nothing rather than an error (see noiseTagDedupeKey).
      await tx
        .insert(noiseTag)
        .values({
          ...tag,
          dedupeKey: noiseTagDedupeKey(tag),
          createdByViewerId: viewerId,
        })
        .onConflictDoNothing();
    });
  });

// Edits a comment — what the dialog its label opens saves: what it says and when. Only a
// comment: an ignored range is retimed by taking it back and sweeping again. Its key follows
// the words and the times (see noiseTagDedupeKey), so an edit that makes it the twin of another
// comment is refused rather than leaving two of the same. A null end is a marker.
export const updateNoiseTag = createServerFn()
  .middleware([crewAuth])
  .validator(
    z
      .object({
        tagId: z.string().min(1),
        text: z.string().trim().min(1).max(2000),
        start: z.number().int(),
        end: z.number().int().nullable(),
      })
      .refine((v) => v.end == null || v.end > v.start, {
        message: END_BEFORE_START,
        path: ['end'],
      }),
  )
  .handler(async ({data}) => {
    const tag = await db.query.noiseTag.findFirst({
      where: and(eq(noiseTag.id, data.tagId), eq(noiseTag.type, 'COMMENT')),
    });
    if (!tag) throw notFound();
    const next = {
      ...tag,
      text: data.text,
      start: new Date(data.start),
      end: instantOrNull(data.end),
    };
    try {
      await db
        .update(noiseTag)
        .set({
          text: next.text,
          start: next.start,
          end: next.end,
          dedupeKey: noiseTagDedupeKey(next),
        })
        .where(eq(noiseTag.id, tag.id));
    } catch (error) {
      if (isUniqueViolation(error)) {
        throw new Error('The same comment is already there');
      }
      throw error;
    }
  });

// Takes a tag back — what the chart's "Un-ignore" confirms. deleteMany, so a double press
// or a stale page deletes nothing twice.
export const deleteNoiseTag = createServerFn()
  .middleware([crewAuth])
  .validator(z.object({tagId: z.string().min(1)}))
  .handler(async ({data: {tagId}}) => {
    await db.delete(noiseTag).where(eq(noiseTag.id, tagId));
  });

// The bands overlapping a project's window, so a set that started before the monitors went
// up still counts — the one filter the lineup dialog previews with and the import writes
// from, so the two cannot list different bands.
const bandsDuring = (project: {start: Date; end: Date}) =>
  and(
    lt(bandPlaying.startTime, project.end),
    gt(bandPlaying.endTime, project.start),
  );

// The festival's stages with the bands playing on each while a project runs — what the
// location card's "Import band lineup…" offers to pin to a place. Any band overlapping the
// project's window, so a set that started before the monitors went up still counts. Only
// areas with someone playing in that window: an empty stage is nothing to import.
export const noiseLineupAreas = createServerFn()
  .middleware([crewAuth])
  .validator(z.object({projectId: z.string().min(1)}))
  .handler(async ({data: {projectId}}) => {
    const project = await db.query.noiseProject.findFirst({
      where: eq(noiseProject.id, projectId),
      columns: {start: true, end: true},
    });
    if (!project) throw notFound();
    const areas = await db.query.area.findMany({
      orderBy: asc(area.order),
      columns: {id: true, displayName: true},
      with: {
        bandPlayings: {
          where: bandsDuring(project),
          orderBy: asc(bandPlaying.startTime),
          columns: {id: true, name: true, startTime: true, endTime: true},
        },
      },
    });
    return areas
      .filter((a) => a.bandPlayings.length > 0)
      .map((a) => ({
        id: a.id,
        displayName: a.displayName,
        bands: a.bandPlayings.map((b) => ({
          id: b.id,
          name: b.name,
          start: b.startTime.getTime(),
          end: b.endTime.getTime(),
        })),
      }));
  });

// One comment on a place per band playing on a stage while the project runs, named for the
// band and spanning its set. Pinned to the location, since that is the card it was asked
// from. A band already there — a comment at this place with its name over the same set — is
// skipped by the tags' unique key, so importing the same stage twice adds nothing.
export const importNoiseLineup = createServerFn()
  .middleware([crewAuth])
  .validator(
    z.object({
      projectId: z.string().min(1),
      locationId: z.string().min(1),
      areaId: z.string().min(1),
    }),
  )
  .handler(async ({data, context}) => {
    const viewerId = context.viewer?.id;
    if (!viewerId) throw new Error('Unauthorized');
    const location = await db.query.noiseLocation.findFirst({
      where: and(
        eq(noiseLocation.id, data.locationId),
        eq(noiseLocation.projectId, data.projectId),
      ),
      columns: {id: true},
      with: {noiseProject: {columns: {start: true, end: true}}},
    });
    if (!location) throw notFound();
    const project = location.noiseProject;
    const bands = await db
      .select({
        name: bandPlaying.name,
        startTime: bandPlaying.startTime,
        endTime: bandPlaying.endTime,
      })
      .from(bandPlaying)
      .where(and(eq(bandPlaying.areaId, data.areaId), bandsDuring(project)));
    if (bands.length === 0) {
      return {created: 0, skipped: 0};
    }
    // A band already pinned here is skipped by the unique key rather than looked up first,
    // so two imports racing each other still write it once (see noiseTagDedupeKey).
    const created = await db
      .insert(noiseTag)
      .values(
        bands.map((b) => {
          const tag = {
            projectId: data.projectId,
            locationId: data.locationId,
            deviceId: null,
            type: 'COMMENT' as const,
            start: b.startTime,
            end: b.endTime,
            text: b.name,
          };
          return {
            ...tag,
            dedupeKey: noiseTagDedupeKey(tag),
            createdByViewerId: viewerId,
          };
        }),
      )
      .onConflictDoNothing()
      .returning({id: noiseTag.id});
    return {created: created.length, skipped: bands.length - created.length};
  });

export const Route = createFileRoute('/crew/noise')({
  component: NoiseLayout,
  head: () => seo({title: 'Noise'}),
});

function NoiseLayout() {
  // The seam between the two pipelines, and the reason it's a ref: the BLE hook
  // writes the connected device's name here so the MQTT stream can skip its
  // duplicate copies, and a ref means connecting doesn't re-run the stream's
  // effect and drop the broker connection mid-session.
  const connectedDevice = useRef<string | null>(null);

  const {live, deviceData, ingest} = useNoiseStream({
    skipDevice: connectedDevice,
  });
  const bluetooth = useBleDevice({ingest, connectedDevice});

  // Warn before navigating away or reloading while connected over Bluetooth —
  // leaving the page tears down the BLE connection. Navigating between pages
  // under /crew/noise keeps the layout (and the connection) mounted, so
  // those moves should not be blocked.
  useBlocker({
    disabled: !bluetooth.deviceName,
    enableBeforeUnload: () => bluetooth.deviceName != null,
    shouldBlockFn: ({next}) => {
      if (next.fullPath.startsWith(Route.fullPath)) return false;
      return !window.confirm(
        'You are connected over Bluetooth. Leaving this page disconnects it. Continue anyway?',
      );
    },
  });

  return (
    // Both of these are the same object for the life of the layout — the buffers a
    // ref, the live records a store subscribed to by device name — so neither provider
    // ever hands its consumers a new value.
    <NoiseBuffersContext.Provider value={deviceData}>
      <NoiseLiveContext.Provider value={live}>
        <BluetoothContext.Provider value={bluetooth}>
          {/* The area's dark scope, and the whole of it. <Theme> is Chakra's own
            wrapper for this: it sets the class the `_dark` condition looks for, the
            native `color-scheme` a class can't reach (the scrollbar just below, and
            the <select> the header's view switch falls back to on phones), and
            paints `bg` — so it replaces the plain Box this used to be rather than
            adding an element.

            Scoped here rather than on <html> so that everything Chakra portals to
            <body> — the dialogs, the menus, the popovers, the toaster below — lands
            outside it and renders light. The two floating panels are the one
            exception and ask for dark themselves: they draw canvas charts in fixed
            dark hexes (see theme-noise). */}
          {/* Tabular figures for the whole area, inherited rather than repeated on
            every readout. The levels, the clock and the battery all change in place
            — a proportional '1' is narrower than a '4', so without this every number
            on the page reflows as it updates, and a dB value twitches once a second.
            The monospace they used to be set in was doing this by accident; this is
            the half of it that was actually wanted.

            Portalled surfaces don't inherit it (they hang off <body>): the menus and
            dialogs show identifiers rather than ticking numbers, so they don't need
            it — ReferenceMicPanel is the exception and sets it itself. */}
          {/* No padding of its own: the page inside decides where its edges are. The
              project page is a toolbar over an edge-to-edge map, which a gutter here
              would either cut into or leave scrolling content peeking past; the pages
              that do want one set it themselves. This box is only the viewport height
              and the one thing that scrolls — which is also what the toolbars stick to.

              `overscrollBehavior` because the ground stops at this box now: globalCss
              paints html/body too, but with the *light* `bg`, so a rubber-banded
              scroll would show a pale gutter past the edges on iOS. Containing the
              scroll chain keeps the bounce inside the dark scope.

              `dvh` rather than `vh`, and that unit is the whole of why the foot bars
              are reachable on a phone: `100vh` is the *large* viewport, the height the
              page would have with Safari's chrome collapsed, so the box overhangs the
              visible one by the height of that chrome. body sets no height, so the
              overhang becomes a second scroll of its own and the bars that stick to
              the foot of *this* box stick to an edge underneath the toolbar. Sized to
              what is actually visible, it fits — so body never overflows, the chrome
              is never scrolled away, and the unit doesn't move under the layout.
              `svh` would be the pale gutter again, in the one state where it differs. */}
          <Theme
            appearance="dark"
            fontVariantNumeric="tabular-nums"
            h="100dvh"
            display="flex"
            flexDirection="column"
            overflow="auto"
            overscrollBehavior="contain"
          >
            <Outlet />
          </Theme>
          <Toaster />
        </BluetoothContext.Provider>
      </NoiseLiveContext.Provider>
    </NoiseBuffersContext.Provider>
  );
}
